import apiClient from './client'
import type {
  MetricSnapshot,
  MetricAlert,
  MetricSummary,
  AvailableMetricsResponse,
  MetricCondition,
} from '../types'

export interface MetricsListResponse {
  snapshots: MetricSnapshot[]
  total: number
}

export interface MetricAlertsResponse {
  alerts: MetricAlert[]
  total: number
}

export interface MetricAlertCreate {
  metric_name: string
  condition: MetricCondition
  threshold?: number | null // Required for threshold-based, null for anomaly-based
}

export interface MetricAlertUpdate {
  condition?: MetricCondition
  threshold?: number | null
  is_active?: boolean
}

export interface MessageResponse {
  message: string
}

export const metricsApi = {
  list: async (
    appId: string,
    params?: {
      hours?: number
      skip?: number
      limit?: number
    }
  ): Promise<MetricsListResponse> => {
    const response = await apiClient.get<MetricsListResponse>(`/apps/${appId}/metrics`, {
      params,
    })
    return response.data
  },

  getLatest: async (appId: string): Promise<MetricSnapshot | null> => {
    try {
      const response = await apiClient.get<MetricSnapshot | null>(`/apps/${appId}/metrics/latest`)
      return response.data
    } catch {
      // No metrics yet - return null instead of throwing
      return null
    }
  },

  getSummary: async (appId: string, metricName: string, hours = 24): Promise<MetricSummary> => {
    const response = await apiClient.get<MetricSummary>(
      `/apps/${appId}/metrics/summary/${metricName}`,
      { params: { hours } }
    )
    return response.data
  },

  getAvailable: async (appId: string): Promise<AvailableMetricsResponse> => {
    const response = await apiClient.get<AvailableMetricsResponse>(
      `/apps/${appId}/metrics/available`
    )
    return response.data
  },

  listAlerts: async (
    appId: string,
    params?: {
      skip?: number
      limit?: number
    }
  ): Promise<MetricAlertsResponse> => {
    const response = await apiClient.get<MetricAlertsResponse>(`/apps/${appId}/metrics/alerts`, {
      params,
    })
    return response.data
  },

  createAlert: async (appId: string, data: MetricAlertCreate): Promise<MetricAlert> => {
    const response = await apiClient.post<MetricAlert>(`/apps/${appId}/metrics/alerts`, data)
    return response.data
  },

  updateAlert: async (
    appId: string,
    alertId: string,
    data: MetricAlertUpdate
  ): Promise<MetricAlert> => {
    const response = await apiClient.put<MetricAlert>(
      `/apps/${appId}/metrics/alerts/${alertId}`,
      data
    )
    return response.data
  },

  deleteAlert: async (appId: string, alertId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(
      `/apps/${appId}/metrics/alerts/${alertId}`
    )
    return response.data
  },
}

export type { MetricSnapshot, MetricAlert, MetricSummary }
